/**
 * ExecutiveReportsList — table of generated executive PDFs.
 * Used in both the Client Portal tab and the Admin route.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, Trash2 } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return format(new Date(iso), "d MMM yyyy · HH:mm", { locale: es });
    } catch {
        return iso.slice(0, 16);
    }
};

export default function ExecutiveReportsList({ reports = [], onDelete, showClient = false, clientLookup = {} }) {
    if (reports.length === 0) {
        return (
            <div className="text-center py-8 text-sm text-muted-foreground border border-dashed rounded-lg" data-testid="exec-reports-empty">
                <FileText className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                Aún no se han generado informes. Usa el botón <b>"Generar informe ejecutivo"</b> para crear el primero.
            </div>
        );
    }
    return (
        <div className="space-y-2" data-testid="exec-reports-list">
            {reports.map((r) => (
                <div
                    key={r.id}
                    className="border rounded-lg p-3 flex items-start justify-between gap-3 hover:bg-slate-50 transition"
                    data-testid={`exec-report-row-${r.id}`}
                >
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <FileText className="w-4 h-4 text-indigo-600 shrink-0" />
                            <span className="font-semibold text-sm">{r.period_label}</span>
                            <Badge variant="secondary" className="text-[10px]">
                                {r.floor_plan_ids?.length || 0} {r.floor_plan_ids?.length === 1 ? "plano" : "planos"}
                            </Badge>
                            {r.generated_by_role === "admin" && (
                                <Badge variant="outline" className="text-[10px] border-purple-300 text-purple-700">Admin</Badge>
                            )}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1 flex gap-2 flex-wrap">
                            <span>Generado el {fmtDate(r.generated_at)}</span>
                            {showClient && (
                                <>
                                    <span>·</span>
                                    <span>Cliente: <b>{clientLookup[r.client_id] || r.client_id?.slice(0, 8)}</b></span>
                                </>
                            )}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1 flex gap-2 flex-wrap">
                            {r.totals_snapshot && (
                                <>
                                    <span>Capturas: <b>{r.totals_snapshot.captures}</b></span>
                                    <span>· Cebos: <b>{r.totals_snapshot.consumed}</b></span>
                                    <span>· UV: <b>{r.totals_snapshot.uv_insects}</b></span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                        <a href={assetUrl(r.pdf_url)} target="_blank" rel="noreferrer" data-testid={`exec-report-download-${r.id}`}>
                            <Button size="sm" className="bg-indigo-900 text-white hover:bg-indigo-800">
                                <Download className="w-3.5 h-3.5 mr-1" /> Descargar
                            </Button>
                        </a>
                        {onDelete && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onDelete(r)}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`exec-report-delete-${r.id}`}
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}
